export type BiomarkerAlias = { pattern: string; source: string; confidence: number };

export type Biomarker = {
  name: string;                 // canonical, lowercase
  primary_domain?: string;
  consumer_domains?: string[];
  data_source?: string;
  formulation_relevant?: boolean;
  measurable?: boolean;
  recency_days?: number | null;
  aliases?: BiomarkerAlias[];
};

export type BiomarkersResponse = {
  biomarkers: Biomarker[];
  biomarker_count: number;
  alias_count: number;
};

// Diff a canonical's alias patterns (original vs edited) into the per-pattern
// PUT/DELETE calls the backend expects. Patterns are normalised the way the
// backend stores them: trimmed, lowercased, de-duplicated, blanks dropped.
export function diffAliasPatterns(
  original: string[], next: string[],
): { toAdd: string[]; toRemove: string[] } {
  const norm = (xs: string[]) =>
    Array.from(new Set(xs.map((s) => s.trim().toLowerCase()).filter(Boolean)));
  const orig = norm(original);
  const nxt = norm(next);
  const origSet = new Set(orig);
  const nxtSet = new Set(nxt);
  return {
    toAdd: nxt.filter((p) => !origSet.has(p)),
    toRemove: orig.filter((p) => !nxtSet.has(p)),
  };
}

// Group biomarkers by their primary domain — the grouping tier of the data
// hierarchy. Domains are derived from biomarker_db here (no separate endpoint).
export type DomainGroup = { domain: string; biomarkers: Biomarker[] };
export function groupByDomain(list: Biomarker[]): DomainGroup[] {
  const map = new Map<string, Biomarker[]>();
  for (const b of list) {
    const d = b.primary_domain || 'unassigned';
    const bucket = map.get(d) ?? (map.set(d, []), map.get(d)!);
    bucket.push(b);
  }
  return [...map.entries()]
    .map(([domain, biomarkers]) => ({ domain, biomarkers }))
    .sort((a, b) => a.domain.localeCompare(b.domain));
}

export function filterBiomarkers(list: Biomarker[], query: string): Biomarker[] {
  const q = query.toLowerCase().trim();
  if (!q) return list;
  return list.filter((b) =>
    b.name.toLowerCase().includes(q) ||
    (b.primary_domain || '').toLowerCase().includes(q) ||
    (b.aliases || []).some((a) => a.pattern.toLowerCase().includes(q)));
}
